import axios from 'axios';

const membersRequestsByOrganizationId = new Map();

class OrganizationService {
  async inviteMember(userData) {
    try {
      const response = await axios.post('/users/invite', userData);
      return response.data;
    } catch (error) {
      console.error('Error inviting user:', error);
      throw error;
    }
  }
  async getMembers(organizationId) {
    if (membersRequestsByOrganizationId.has(organizationId)) {
      return membersRequestsByOrganizationId.get(organizationId);
    }

    const request = (async () => {
      const response = await axios.get(`/organizations/${organizationId}/members`);
      const body = response.data;
      // Normalize: API returns { success: true, data: [...] } – always return the members array
      if (Array.isArray(body?.data)) return body.data;
      if (Array.isArray(body)) return body;
      if (Array.isArray(body?.members)) return body.members;
      return [];
    })();

    membersRequestsByOrganizationId.set(organizationId, request);

    try {
      return await request;
    } catch (error) {
      console.error('Error fetching members:', error);
      throw error;
    } finally {
      membersRequestsByOrganizationId.delete(organizationId);
    }
  }

  async updateMemberRole(organizationId, memberId, role) {
    try {
      const response = await axios.patch(`/organizations/${organizationId}/members/${memberId}`, {
        role,
      });
      return response.data;
    } catch (error) {
      console.error('Error updating member role:', error);
      throw error;
    }
  }

  /**
   * Per-member planning overrides (werktijden / inplanbaar). Pass `null` to
   * reset to the organization defaults.
   */
  async updateMemberPlanningSettings(organizationId, memberId, planningSettings) {
    const response = await axios.patch(`/organizations/${organizationId}/members/${memberId}`, {
      planning_settings: planningSettings,
    });
    return response.data?.data || response.data;
  }

  async getInvites(organizationId) {
    try {
      const response = await axios.get(`/organizations/${organizationId}/invites`);
      return response.data;
    } catch (error) {
      console.error('Error fetching invites:', error);
      throw error;
    }
  }

  async cancelInvite(inviteId) {
    try {
      const response = await axios.delete(`/users/invite/${inviteId}`);
      return response.data;
    } catch (error) {
      console.error('Error cancelling invite:', error);
      throw error;
    }
  }

  async resendInvite(inviteId) {
    try {
      const response = await axios.post(`/users/invite/${inviteId}/resend`);
      return response.data;
    } catch (error) {
      console.error('Error resending invite:', error);
      throw error;
    }
  }

  async update(organizationId, data) {
    try {
      const response = await axios.patch(`/organizations/${organizationId}`, data);
      return response.data;
    } catch (error) {
      console.error('Error updating organization:', error);
      throw error;
    }
  }

  async getProposalTemplates() {
    try {
      const response = await axios.get('/organizations/proposal-templates');
      return response.data;
    } catch (error) {
      console.error('Error fetching proposal templates:', error);
      throw error;
    }
  }

  async updateProposalTemplates(data) {
    try {
      const response = await axios.patch('/organizations/proposal-templates', data);
      return response.data;
    } catch (error) {
      console.error('Error updating proposal templates:', error);
      throw error;
    }
  }

  async getApiKeys(organizationId, { includeRevoked = false } = {}) {
    const response = await axios.get(`/organizations/${organizationId}/api-keys`, {
      params: { include_revoked: includeRevoked ? 'true' : 'false' },
    });
    return response.data?.data?.keys || [];
  }

  async createApiKey(organizationId, name) {
    const response = await axios.post(`/organizations/${organizationId}/api-keys`, { name });
    return response.data?.data || response.data;
  }

  async revokeApiKey(organizationId, keyId) {
    const response = await axios.post(
      `/organizations/${organizationId}/api-keys/${keyId}/revoke`
    );
    return response.data?.data?.key || response.data;
  }
}

export default OrganizationService;
